import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  MenuItem,
  Button,
  Badge,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CasesOutlinedIcon from '@mui/icons-material/CasesOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import AccessibilityOutlinedIcon from '@mui/icons-material/AccessibilityOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';

/**
 * Componente de menu responsivo.
 * Mobile: menu hamburger com drawer lateral
 * Desktop: barra horizontal com dropdowns
 *
 * @component
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.data - Dados do menu em formato JSON
 * @param {Function} props.onAction - Callback para ações do menu (Observer)
 * @returns {JSX.Element} Menu responsivo
 */
export default function Menu({ data, onAction }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const favorites = useSelector(state => state.favorites.items || []);
  const { user } = useSelector(state => state.auth);

  /**
   * Filtra menu recursivamente baseado nas roles do usuário.
   * Implementa filtragem imutável para garantir recalculação correta do useMemo.
   */
  const filteredMenuData = useMemo(() => {
    console.log('🎯 Filtrando menu com usuário:', user);
    if (!data?.menu) return data;
    
    /**
     * Verifica se usuário tem acesso ao item do menu
     */
    const hasAccess = (item) => {
      console.log('🔍 Verificando acesso:', { 
        item: item.label, 
        accessRole: item.accessRole, 
        userRoles: user?.roles 
      });
      
      if (!item.accessRole) return true;
      if (item.accessRole.includes('All')) return true;
      if (!user?.roles) {
        console.log('❌ Usuário sem roles');
        return false;
      }
      
      const hasPermission = item.accessRole.some(role => 
        user.roles.some(userRole => {
          console.log('🔄 Comparando:', { role, userRole: userRole.alias });
          return userRole.alias === role;
        })
      );
      
      console.log('✅ Resultado acesso:', hasPermission);
      return hasPermission;
    };
    
    /**
     * Filtra menu de forma imutável (sem mutar objetos originais)
     */
    const filterMenuByRoles = (menuItems) => {
      return menuItems
        .filter(item => hasAccess(item))
        .map(item => {
          if (item.submenu) {
            return {
              ...item,
              submenu: filterMenuByRoles(item.submenu)
            };
          }
          return item;
        });
    };
    
    const filtered = {
      ...data,
      menu: filterMenuByRoles([...data.menu])
    };
    
    console.log('📋 Menu filtrado:', filtered);
    return filtered;
  }, [data, user]);

  /** @type {[boolean, function]} Estado do drawer mobile */
  const [mobileOpen, setMobileOpen] = useState(false);

  /** @type {[Object, function]} Estado dos submenus expandidos mobile */
  const [expandedItems, setExpandedItems] = useState({});

  /** @type {[Object, function]} Estado dos dropdowns desktop */
  const [openDropdowns, setOpenDropdowns] = useState({});

  /** @type {[string|null, function]} Estado do item com hover */
  const [hoveredItem, setHoveredItem] = useState(null);

  /**
   * Alterna expansão de submenu mobile
   */
  const handleToggleExpand = itemId => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  /**
   * Manipula dropdown desktop
   */
  const handleDropdownToggle = (itemId, event) => {
    event.stopPropagation();
    setOpenDropdowns(prev => {
      const isCurrentlyOpen = prev[itemId];
      return isCurrentlyOpen ? {} : { [itemId]: true };
    });
  };

  /**
   * Manipula clique em item do menu
   */
  const handleMenuClick = (item, event) => {
    if (event) event.stopPropagation();
    if (item.action && onAction) {
      onAction(item.action);
    }
    setMobileOpen(false);
    setOpenDropdowns({});
  };

  /**
   * Obtém o componente de ícone baseado no nome
   * @param {string} iconName - Nome do ícone
   * @returns {JSX.Element|null} Componente do ícone
   */
  const getIconComponent = iconName => {
    const iconMap = {
      home: HomeOutlinedIcon,
      shopping_cart: () => <Box component="img" src="/imagens/icons/svg/vikanaweb-shopping-bag.svg" alt="Carrinho" sx={{ width: 24, height: 24 }} />,
      cases: CasesOutlinedIcon,
      sell: LabelOutlinedIcon,
      brand: SellOutlinedIcon,
      category: CategoryOutlinedIcon,
      person: PersonOutlinedIcon,
      favorite: FavoriteBorderOutlinedIcon,
      accessibility: AccessibilityOutlinedIcon,
      settings: SettingsSuggestOutlinedIcon,
      role: PeopleOutlineOutlinedIcon,
      product: ShoppingBagOutlinedIcon,
      store: StoreOutlinedIcon,
      sync: SyncOutlinedIcon
    };

    const IconComponent = iconMap[iconName];
    return IconComponent ? (typeof IconComponent === 'function' && IconComponent.name === '' ? <IconComponent /> : <IconComponent />) : null;
  };

  /**
   * Renderiza item mobile recursivamente
   */
  const renderMobileItem = (item, level = 0, parentId = '') => {
    const itemId = `${parentId}-${item.label}`;
    const hasSubmenu = item.submenu?.length > 0;
    const isExpanded = expandedItems[itemId];

    return (
      <Box key={itemId}>
        <ListItem disablePadding sx={{ pl: level * 2, pb: 0 }}>
          <ListItemButton
            onClick={() => (hasSubmenu ? handleToggleExpand(itemId) : handleMenuClick(item))}
            data-testid={`menu-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {item.icon ? (
                item.icon === 'favorite' ? (
                  <Badge badgeContent={favorites?.length || 0} color='secondary'>
                    {getIconComponent(item.icon)}
                  </Badge>
                ) : (
                  getIconComponent(item.icon)
                )
              ) : (
                <Box sx={{ width: 24, height: 24 }} />
              )}
            </ListItemIcon>
            <ListItemText primary={item.label} />
            {hasSubmenu && (isExpanded ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />)}
          </ListItemButton>
        </ListItem>
        {hasSubmenu && (
          <Collapse in={isExpanded} timeout='auto' unmountOnExit>
            <List component='div' disablePadding>
              {item.submenu.map(subItem => renderMobileItem(subItem, level + 1, itemId))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  /**
   * Renderiza item desktop com dropdown
   */
  const renderDesktopItem = (item, index) => {
    const hasSubmenu = item.submenu?.length > 0;
    const isOpen = openDropdowns[item.label];

    return (
      <Box key={item.label} sx={{ position: 'relative', display: 'inline-block' }}>
        <Button
          onClick={e =>
            hasSubmenu ? handleDropdownToggle(item.label, e) : handleMenuClick(item, e)
          }
          onMouseEnter={() => setHoveredItem(item.label)}
          onMouseLeave={() => setHoveredItem(null)}
          startIcon={
            item.icon ? (
              item.icon === 'favorite' ? (
                <Badge badgeContent={favorites?.length || 0} color='secondary'>
                  {getIconComponent(item.icon)}
                </Badge>
              ) : (
                getIconComponent(item.icon)
              )
            ) : null
          }
          endIcon={hasSubmenu ? <KeyboardArrowDownOutlinedIcon /> : null}
          sx={{
            color: 'inherit',
            textTransform: 'none',
            px: 2,
            py: 1,
            position: 'relative',
            fontFamily: theme.typography.fontFamily,
            '&:focus': {
              outline: 'none',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: hoveredItem === item.label ? '80%' : '0%',
              height: '2px',
              bgcolor: 'secondary.main',
              transition: 'width 0.3s ease',
            },
          }}
        >
          {item.label}
        </Button>
        {hasSubmenu && isOpen && (
          <Box
            onClick={e => e.stopPropagation()}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              bgcolor: 'rgba(205, 201, 201, 0.92)',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              borderRadius: 1,
              minWidth: 200,
              zIndex: 1300,
            }}
          >
            {item.submenu.map(subItem => renderDropdownItem(subItem))}
          </Box>
        )}
      </Box>
    );
  };

  /**
   * Renderiza item do dropdown recursivamente
   */
  const renderDropdownItem = (item, level = 0) => {
    const hasSubmenu = item.submenu?.length > 0;

    return (
      <Box key={item.label}>
        <MenuItem
          onClick={e => handleMenuClick(item, e)}
          sx={{
            pl: level * 2 + 2,
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.9rem',
            color: '#FFFFFF',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.15)',
            },
          }}
          data-testid={`menu-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {item.icon && (
            <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', minWidth: 24, color: '#FFFFFF', '& svg': { fontSize: '1.35rem', color: '#FFFFFF' } }}>
              {getIconComponent(item.icon)}
            </Box>
          )}
          {item.label}
        </MenuItem>
        {hasSubmenu && item.submenu.map(subItem => renderDropdownItem(subItem, level + 1))}
      </Box>
    );
  };

  if (!filteredMenuData?.menu) return null;

  return (
    <Box sx={{ width: '100%' }}>
      {isMobile ? (
        <>
          <IconButton color='inherit' aria-label='open menu' onClick={() => setMobileOpen(true)}>
            <MenuOutlinedIcon sx={{ width: '4vh', height: '4vh', p: 0, color: '#B5745D' }} />
          </IconButton>
          <Drawer
            variant='temporary'
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              zIndex: 1400,
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: 280,
                zIndex: 1400,
              },
            }}
          >
            <List>
              {filteredMenuData.menu.map((item, index) => renderMobileItem(item, 0, `root-${index}`))}
            </List>
          </Drawer>
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            px: 2,
            py: 1,
            position: 'relative',
          }}
          onClick={() => setOpenDropdowns({})}
          onMouseLeave={() => {
            setOpenDropdowns({});
            setHoveredItem(null);
          }}
        >
          {filteredMenuData.menu.map((item, index) => renderDesktopItem(item, index))}
        </Box>
      )}
    </Box>
  );
}
